import { api } from './api';

/**
 * Comp vouchers and season passes (D-373), kept beside api.ts like
 * api-seasons.ts.
 */

export interface VoucherBatchRow {
  id: string;
  name: string;
  season_id: string;
  season_name: string;
  comp_reason_id: string;
  comp_reason_name: string;
  quantity: number;
  admits: number;
  ticket_type_code: string;
  price_zone_codes_json: string | null;
  expires_on: string | null;
  effective_expires_on: string;
  terms_text: string | null;
  created_at: string;
  voided_at: string | null;
  void_reason: string | null;
  redeemed?: number;
}

export interface VoucherRow {
  id: string;
  code: string;
  sequence: number;
  status: 'OUTSTANDING' | 'REDEEMED' | 'EXPIRED' | 'VOIDED';
  voided_at: string | null;
  void_reason: string | null;
  order_id: string | null;
  order_number: string | null;
  redeemed_at: string | null;
  channel: string | null;
  event_title: string | null;
  starts_at: string | null;
}

export interface VoucherGame {
  event_id: string;
  title: string;
  performance_id: string;
  starts_at: string;
  eligible: boolean;
  reason: string | null;
}

export interface BatchReport {
  batch: VoucherBatchRow & { price_zone_codes: string[] | null };
  counts: { issued: number; redeemed: number; outstanding: number; expired: number; voided: number };
  vouchers: VoucherRow[];
  games: VoucherGame[];
}

export interface NewVoucherBatch {
  name: string;
  season_id: string;
  comp_reason_id?: string | null;
  quantity: number;
  admits: number;
  ticket_type_code: string;
  price_zone_codes?: string[] | null;
  expires_on?: string | null;
  terms_text?: string | null;
  prefix?: string;
  length?: number;
}

export const voucherEndpoints = {
  options: () => api.get<{ comp_reasons: { id: string; code: string; name: string }[]; seasons: { id: string; name: string; starts_on: string; ends_on: string }[] }>('/api/admin/vouchers/options'),
  batches: (seasonId?: string) => api.get<{ batches: VoucherBatchRow[] }>(`/api/admin/vouchers/batches${seasonId ? `?season_id=${seasonId}` : ''}`),
  create: (body: NewVoucherBatch) => api.post<{ batch: VoucherBatchRow }>('/api/admin/vouchers/batches', body),
  report: (id: string) => api.get<BatchReport>(`/api/admin/vouchers/batches/${id}`),
  voidBatch: (id: string, reason: string) => api.post<{ batch: VoucherBatchRow }>(`/api/admin/vouchers/batches/${id}/void`, { reason }),
  voidVoucher: (batchId: string, voucherId: string, reason: string) => api.post<{ voided: true }>(`/api/admin/vouchers/batches/${batchId}/vouchers/${voucherId}/void`, { reason }),
  csvUrl: (id: string) => `/api/admin/vouchers/batches/${id}/vouchers.csv`,
  sheetUrl: (id: string, range?: { from?: number; to?: number; all?: boolean }) => {
    const query = new URLSearchParams();
    if (range?.from) query.set('from', String(range.from));
    if (range?.to) query.set('to', String(range.to));
    if (range?.all) query.set('all', '1');
    const text = query.toString();
    return `/api/admin/vouchers/batches/${id}/sheet.pdf${text ? `?${text}` : ''}`;
  },
};

export interface SeasonPassRow {
  id: string;
  number: string;
  season_id: string;
  season_name: string;
  holder_sub: string;
  holder_name: string | null;
  holder_email: string | null;
  label: string;
  issue_group_id: string;
  comp_reason_name: string;
  ticket_type_code: string;
  valid_from: string;
  valid_to: string;
  status: string;
  status_reason: string | null;
  credential_version: number;
  games_attended?: number;
  tickets?: number;
}

export interface PassGame {
  event_id: string;
  title: string;
  starts_at: string;
  ticket_id: string;
  order_number: string;
  channel: string;
  seat_label: string | null;
  item_status: string;
  ticket_status: string;
  admitted_at: string | null;
}

export interface IssuePlan {
  dry_run: boolean;
  season: { id: string; name: string };
  valid_from: string;
  valid_to: string;
  holders: { name: string; email: string; passes: number; labels: string[] }[];
  problems: { line: number; message: string }[];
  total_passes: number;
  issued?: { holder: { customer_id: string; name: string; email: string; created: boolean }; pass_ids: string[]; emailed: boolean }[];
  passes_created?: number;
}

export const seasonPassEndpoints = {
  options: () => api.get<{ comp_reasons: { id: string; code: string; name: string }[]; seasons: { id: string; name: string; starts_on: string; ends_on: string }[] }>('/api/admin/season-passes/options'),
  list: (seasonId: string, q: string) => api.get<{ passes: SeasonPassRow[] }>(`/api/admin/season-passes?season_id=${encodeURIComponent(seasonId)}${q ? `&q=${encodeURIComponent(q)}` : ''}`),
  issue: (body: { season_id: string; ticket_type_code: string; comp_reason_id?: string | null; valid_from?: string | null; valid_to?: string | null; text?: string; holders?: { name: string; email: string; passes: number; names?: string[] }[]; send_email: boolean; dry_run: boolean }) =>
    api.post<IssuePlan>('/api/admin/season-passes/issue', body),
  detail: (id: string) => api.get<{ pass: SeasonPassRow; games: PassGame[]; history: { event_type: string; credential_version: number; reason: string | null; occurred_at: string; staff: string | null }[]; household: { id: string; label: string; status: string }[]; credential: string | null }>(`/api/admin/season-passes/${id}`),
  change: (id: string, action: 'suspend' | 'reactivate' | 'revoke' | 'reissue', body: { reason?: string | null; send_email?: boolean }) => api.post<{ pass: SeasonPassRow }>(`/api/admin/season-passes/${id}/${action}`, body),
  emailHolder: (sub: string, seasonId: string) => api.post<{ passes: number; to_email: string }>(`/api/admin/season-passes/holders/${sub}/email`, { season_id: seasonId }),
  googleWallet: (id: string) => api.get<{ url: string }>(`/api/admin/season-passes/${id}/wallet/google`),
  pdfUrl: (id: string) => `/api/admin/season-passes/${id}/pdf`,
  holderPdfUrl: (sub: string, seasonId: string) => `/api/admin/season-passes/holders/${sub}/pdf?season_id=${encodeURIComponent(seasonId)}`,
  appleUrl: (id: string) => `/api/admin/season-passes/${id}/wallet/apple`,
};
